<template>
  <DxPopup v-bind="popupConfig" @shown="onShown" @hidden="onHidden">
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :toolbar="item.position"
      :visible="item.visible"
      :options="item.options"
    />
    <template #content>
      <div
        class="css-v2 gc-popup-form"
        style="height: 100%; display: flex; flex-direction: column; padding: 16px; overflow: hidden;"
      >
        <!-- Header summary -->
        <gc-form
          v-if="eligibilityData && !eligibilityData.IsLinkedToDistributionManifest"
          :data-model="headerModel"
          :formItems="getHeaderFormItems"
          :formOptions="{ gcLabelLocation: 'left' }"
          :formMode="'review'"
          :isNormal="true"
          ref="headerForm"
        />

        <!-- Validation summary -->
        <div
          v-if="validationErrors.length"
          class="split-order-validation-summary"
        >
          <strong>Unable to save:</strong>
          <ul>
            <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
          </ul>
        </div>

        <!-- Lines grid — fills remaining modal height -->
        <dx-data-grid
          v-if="eligibilityData && !eligibilityData.IsLinkedToDistributionManifest"
          :data-source="eligibilityData.Lines"
          :show-borders="false"
          :repaint-changes-only="true"
          :two-way-binding-enabled="false"
          :column-auto-width="false"
          :focused-row-enabled="false"
          :hover-state-enabled="false"
          style="flex: 1; min-height: 0; margin-top: 12px;"
          @cell-click="onGridCellClick"
          @toolbar-preparing="onGridToolbarPreparing"
        >
          <DxScrolling
            mode="virtual"
            row-rendering-mode="virtual"
            :preload-enabled="true"
          />
          <DxPaging :enabled="false" />
          <DxPager :visible="false" />
          <!-- Select column — header is a select-all checkbox; cells show per-line checkbox or dash -->
          <DxColumn
            :width="40"
            alignment="center"
            :allow-sorting="false"
            cell-template="selectCellTemplate"
            header-cell-template="selectAllHeaderTemplate"
          />

          <DxColumn
            caption="Line"
            data-field="LineNo"
            :width="45"
            alignment="left"
            :allow-editing="false"
          />
          <DxColumn
            caption="Type"
            data-field="ItemType"
            :width="ColumnWidth.ItemType.width"
            :allow-editing="false"
          />
          <DxColumn
            caption="Item Code"
            data-field="ItemCode"
            :width="ColumnWidth.ProductCode.width"
            :allow-editing="false"
          />
          <DxColumn
            caption="Description"
            data-field="Description"
            :min-width="140"
            :allow-editing="false"
          />
          <DxColumn
            caption="Cust Prod"
            data-field="CustomerProduct"
            :width="ColumnWidth.ProductCode.width"
            :allow-editing="false"
          />
          <DxColumn
            caption="UM"
            data-field="SalesUM"
            :width="ColumnWidth.UnitOfMeasure.width"
            :allow-editing="false"
          />
          <DxColumn
            caption="Ordered Qty"
            data-field="OrderedQty"
            :width="ColumnWidth.Quantity4DecimalPoints.width"
            :allow-editing="false"
            alignment="right"
            :format="'#,##0.0000'"
          />
          <DxColumn
            caption="Delivered Qty"
            data-field="DeliveredQty"
            :width="ColumnWidth.Quantity4DecimalPoints.width"
            :allow-editing="false"
            alignment="right"
            :format="'#,##0.0000'"
          />

          <!-- Split Qty — editable number, 0 when unchecked, defaults to Remainder on check -->
          <DxColumn
            caption="Split Qty"
            :width="ColumnWidth.Quantity4DecimalPoints.width"
            :allow-editing="false"
            alignment="right"
            cell-template="splitQtyCellTemplate"
          />

          <template #selectAllHeaderTemplate>
            <input
              type="checkbox"
              :checked="allEligibleChecked"
              style="pointer-events: auto; cursor: pointer;"
              @click.stop="toggleSelectAll"
            />
          </template>

          <template #selectCellTemplate="{ data }">
            <span
              v-if="!data.data.Eligible"
              class="ineligible-dash"
              :title="data.data.IneligibleReason"
              >–</span
            >
            <input
              v-else
              type="checkbox"
              :checked="isLineChecked(data.data.LineNo)"
              readonly
              style="pointer-events: none;"
            />
          </template>

          <template #splitQtyCellTemplate="{ data }">
            <span v-if="!data.data.Eligible" class="ineligible-dash">–</span>
            <DxNumberBox
              v-else
              :value="getSplitQty(data.data.LineNo)"
              :disabled="!isLineChecked(data.data.LineNo)"
              :min="data.data.Remainder >= 0 ? 0.0001 : data.data.Remainder"
              :max="data.data.Remainder >= 0 ? data.data.Remainder : -0.0001"
              :step="0.0001"
              :format="'#,##0.0000'"
              :element-attr="{
                class: isLineChecked(data.data.LineNo)
                  ? 'gc-editable-cell-color gc-grid-number-box-editor'
                  : 'cell-disabled gc-grid-number-box-editor',
              }"
              :input-attr="{ style: 'font-size:12px;text-align:right;' }"
              @value-changed="(e) => onSplitQtyChanged(e, data.data)"
            />
          </template>
        </dx-data-grid>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import { SalesOrderSplitService } from "@/_services/api/sales/sales-order-split.service";
import { FeatureStorage } from "@/_services/storage";
import ColumnWidth from "@/_helpers/data-grid/ColumnWidth";
import { DxNumberBox } from "devextreme-vue/number-box";

export default {
  name: "gc-popup-sales-order-split",

  components: { DxNumberBox },

  props: {
    salesOrderCode: {
      type: [String, Number],
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:visible", "saved"],

  data() {
    return {
      ColumnWidth,
      loading: false,
      eligibilityData: null,
      // Map of lineNo → splitQty for checked lines
      selectedLines: {},
      validationErrors: [],
    };
  },

  computed: {
    popupConfig() {
      return {
        visible: this.visible,
        title: `Split Sales Order # ${this.salesOrderCode}`,
        width: "75%",
        height: 680,
        dragEnabled: false,
        showCloseButton: true,
        wrapperAttr: { class: "css-v2 gc-popup" },
        showTitle: true,
      };
    },

    headerModel() {
      if (!this.eligibilityData) return {};
      return {
        SalesOrderCode: this.eligibilityData.SalesOrderCode,
        Customer:
          this.eligibilityData.CustomerName ||
          this.eligibilityData.CustomerCode,
        ShipDate: this.eligibilityData.ShipDate,
        RequiredByDate: this.eligibilityData.RequiredByDate,
        Status: this.eligibilityData.FriendlyStatus,
        OrderType: this.eligibilityData.OrderType,
      };
    },

    getHeaderFormItems() {
      return [
        {
          itemType: "group",
          colCount: 3,
          items: [
            { dataField: "SalesOrderCode", label: { text: "Sales Order" } },
            {
              dataField: "ShipDate",
              label: { text: "Ship Date" },
              editorType: "dxDateBox",
              editorOptions: {
                displayFormat: "dd MMM yyyy",
                allowUpdate: false,
              },
            },
            { dataField: "Status", label: { text: "Status" } },
            { dataField: "Customer", label: { text: "Customer" } },
            {
              dataField: "RequiredByDate",
              label: { text: "Required By Date" },
              editorType: "dxDateBox",
              editorOptions: {
                displayFormat: "dd MMM yyyy",
                allowUpdate: false,
              },
            },
            { dataField: "OrderType", label: { text: "Order Type" } },
          ],
        },
      ];
    },

    hasCheckedLines() {
      return Object.keys(this.selectedLines).length > 0;
    },

    canSave() {
      return (
        this.hasCheckedLines &&
        FeatureStorage.checkPermission("SalesOrder", "create")
      );
    },

    allEligibleChecked() {
      if (!this.eligibilityData) return false;
      var eligible = this.eligibilityData.Lines.filter(function (l) { return l.Eligible; });
      return eligible.length > 0 && eligible.every((l) => this.isLineChecked(l.LineNo));
    },

    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          location: "after",
          position: "top",
          visible: FeatureStorage.checkPermission("SalesOrder", "create"),
          options: {
            text: "SAVE",
            type: "default",
            stylingMode: "contained",
            disabled: !this.canSave,
            cssClass: "save-btn",
            onClick: this.attemptSave,
          },
        },
      ];
    },
  },

  methods: {
    async onShown() {
      this.eligibilityData = null;
      this.selectedLines = {};
      this.validationErrors = [];
      await this.loadEligibility();
      if (this.eligibilityData && this.eligibilityData.IsLinkedToDistributionManifest) {
        await this.$alert(
          `Sales Order ${this.salesOrderCode} is associated to a distribution manifest and may not be split. Please remove it from the distribution manifest before attempting to split.`,
          "Split Sales Order",
        );
        this.close();
      }
    },

    onHidden() {
      this.$emit("update:visible", false);
    },

    async loadEligibility() {
      this.loading = true;
      try {
        const response = await SalesOrderSplitService.getEligibility(
          this.salesOrderCode,
        );
        this.eligibilityData = response.data;
      } catch (err) {
        this.$notify("Failed to load Split Order data.", "error");
        this.close();
      } finally {
        this.loading = false;
      }
    },

    isLineChecked(lineNo) {
      return Object.prototype.hasOwnProperty.call(this.selectedLines, lineNo);
    },

    getSplitQty(lineNo) {
      // Show 0 when unchecked; show the stored split qty when checked
      return this.isLineChecked(lineNo) ? this.selectedLines[lineNo] : 0;
    },

    onGridToolbarPreparing(e) {
      e.toolbarOptions.items = e.toolbarOptions.items.filter(
        function (item) { return item.name !== "columnChooserButton"; }
      );
    },

    toggleSelectAll() {
      var self = this;
      if (this.allEligibleChecked) {
        // Deselect all eligible lines
        this.eligibilityData.Lines.forEach(function (line) {
          if (line.Eligible) self.$delete(self.selectedLines, line.LineNo);
        });
      } else {
        // Select all eligible lines, defaulting each to its full remainder
        this.eligibilityData.Lines.forEach(function (line) {
          if (line.Eligible) self.$set(self.selectedLines, line.LineNo, line.Remainder);
        });
      }
      this.validationErrors = [];
    },

    onGridCellClick(e) {
      // Only handle clicks on the Select column (index 0); skip ineligible rows and SplitQty editor clicks
      if (e.columnIndex !== 0 || !e.data || !e.data.Eligible) return;
      const lineNo = e.data.LineNo;
      if (this.isLineChecked(lineNo)) {
        this.$delete(this.selectedLines, lineNo);
      } else {
        this.$set(this.selectedLines, lineNo, e.data.Remainder);
      }
      this.validationErrors = [];
    },

    onSplitQtyChanged(e, lineData) {
      if (this.isLineChecked(lineData.LineNo)) {
        this.$set(this.selectedLines, lineData.LineNo, e.value);
      }
    },

    attemptSave() {
      const errors = this.clientValidate();
      if (errors.length) {
        this.validationErrors = errors;
        return;
      }
      this.validationErrors = [];
      this.$confirm(
        "Are you sure you want to split the selected line(s) into a new Sales Order?",
        "Split Sales Order",
      ).then((confirmed) => {
        if (confirmed) this.executeSplit();
        // Cancel: return to modal with selections intact (no action)
      });
    },

    clientValidate() {
      const errors = [];
      if (!this.hasCheckedLines)
        errors.push("A Sales Order line has NOT been selected for splitting.");

      if (this.eligibilityData) {
        // BR-06: source must retain at least one line with remaining quantity after the split.
        // A partial split (SplitQty < OrderedQty) always leaves the source line in place —
        // only a full move (SplitQty = OrderedQty, no delivered qty) removes the source line.
        var self = this;
        var sourceHasRemainingLines = this.eligibilityData.Lines.some(function (line) {
          if (!self.isLineChecked(line.LineNo)) return true; // unselected line stays
          return self.selectedLines[line.LineNo] < line.OrderedQty; // partial split: source line stays with reduced qty
        });
        if (!sourceHasRemainingLines)
          errors.push(
            "At least one Sales Order line must be left in the current Sales Order.",
          );
      }

      for (const [lineNoStr, splitQty] of Object.entries(this.selectedLines)) {
        const lineNo = parseInt(lineNoStr, 10);
        const lineDef =
          this.eligibilityData &&
          this.eligibilityData.Lines.find((l) => l.LineNo === lineNo);
        if (!lineDef) continue;

        const remainder = lineDef.Remainder;
        const valid =
          remainder >= 0
            ? splitQty > 0 && splitQty <= remainder
            : splitQty < 0 && splitQty >= remainder;

        if (!valid)
          errors.push(
            `Split Qty for line ${lineNo} cannot exceed the undelivered quantity.`,
          );
      }

      return errors;
    },

    async executeSplit() {
      this.loading = true;
      try {
        const lines = Object.entries(this.selectedLines).map(
          ([lineNo, splitQty]) => ({
            LineNo: parseInt(lineNo, 10),
            SplitQty: splitQty,
          }),
        );

        const response = await SalesOrderSplitService.splitOrder({
          SalesOrderCode: this.salesOrderCode,
          Lines: lines,
        });

        const newCode = response.data && response.data.NewSalesOrderCode;
        this.$notify(
          `Sales Order split successfully. New Sales Order ${newCode} created.`,
          "success",
        );
        this.$emit("saved", newCode);
        this.close();
      } catch (err) {
        // ApiService interceptor rejects with error.response (the response object),
        // so err.data is the body — not err.response.data.
        var data = (err && err.data) || (err && err.response && err.response.data);
        var message = "An error occurred while splitting the Sales Order.";
        if (data) {
          if (Array.isArray(data) && data.length > 0) {
            message = data.map(function (e) { return e.ErrorMessage || e; }).join(" ");
          } else if (typeof data === "string" && data.length > 0) {
            message = data;
          }
        }
        this.validationErrors = [message];
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.$emit("update:visible", false);
    },
  },
};
</script>

<style scoped>
.split-order-validation-summary {
  background: #fdecec;
  border: 1px solid #f3b8bd;
  color: #a3222f;
  font-size: 12px;
  padding: 10px 14px;
  border-radius: 2px;
  margin-bottom: 14px;
}
.split-order-validation-summary ul {
  margin: 4px 0 0 18px;
  padding: 0;
}
.ineligible-dash {
  color: #c4c8cd;
  text-align: center;
  display: block;
}
.split-order-note {
  font-size: 11px;
  color: #667085;
  margin-top: 10px;
}
</style>
